import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { AttachmentsDialogService, DocumentService } from "@tylertech/attachments-module";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { Chip, DynamicChip } from "@tylertech/common-components-module";
import { NotesDialogService, NoteService } from "@tylertech/notes-module";
import { EventEmitterHelper, Utilities } from "@tylertech/shared-module";
import { DocumentSearchCriteria, ExecutionResult, GetNoteObjectViewModel, IAttachment, INote, ITcmModel, NoteViewModel, } from "@tylertech/tyler-api-types";
import { Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AdvancedComponentsDialogService } from "../../services";

@Component({
    selector: "note-and-attachment",
    templateUrl: "./noteAndAttachment.tmpl.html",
    styleUrls: [
        "./noteAndAttachment.component.scss"
    ],
    standalone: false
})
export class NoteAndAttachmentComponent implements OnDestroy, OnInit
{
    private readonly $documentApiService = inject(DocumentService);
    private readonly $attachmentsDialogService = inject(AttachmentsDialogService);
    private readonly $notesService = inject(NoteService);
    private readonly $notesDialogService = inject(NotesDialogService);
    private readonly $advancedComponentsDialogService = inject(AdvancedComponentsDialogService);

    private readonly destructor$ = new Subject<void>();

    // TODO - should be readonly
    @Input() disableAttachments: boolean; // was allowAttachments
    // TODO - should be readonly
    @Input() disableNotes: boolean; // was allowNotes
    @Input() readonly disabled: boolean; // was canEdit

    @Input() private get entity(): GetNoteObjectViewModel { return this._entity; }
    private set entity(value: GetNoteObjectViewModel)
    {
        const prev = this._entity;
        this._entity = value;

        if (this._entity && prev?.Id !== this._entity.Id)
        {
            this.getDocumentCount();
            this.updateNoteCount();
        }
    }

    @Input() readonly hideChips: boolean;
    @Input() private readonly hideSubject: boolean;
    // TODO - rename; drop the local prefix...
    // TODO - call updateDocumentCount when given new non-empty value
    @Input() private readonly localAttachments: Array<IAttachment>;
    // TODO - rename; drop the local prefix...
    @Output() readonly localAttachmentsChange = new EventEmitter<Array<IAttachment>>();
    // TODO - rename and fix downstream usages.
    @Input("entityForAttachments") private readonly tcmModel: ITcmModel;

    @Input() private readonly additionalCommands: Array<Command>;

    @Input()
    chips: Array<Chip>;

    /**
     * Allows the parent component to trigger a data refetch for `TableLoadType.InfoTable` instances without needing to
     * know implementation specifics for the data.
     */
    @Input() set fireUpdate(value: Observable<void>)
    {
        this._update?.unsubscribe();
        if (!value)
        {
            return;
        }

        this._update = value
            .pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.getDocumentCount();
                this.updateNoteCount();
            });
    }

    @Output() readonly addNote = new EventEmitter<void>();
    @Output() readonly addAttachment = new EventEmitter<void>();

    private _entity: GetNoteObjectViewModel;
    protected attachmentCount: number;
    private entityAddMode: boolean;
    protected hasTCM: boolean;
    protected noteCount: number;
    private _update?: Subscription;
    commands: Array<Command>;


    /** @deprecated */
    private get hasAttachments(): boolean { return this.attachmentCount > 0; }
    private get hasNotes(): boolean { return this.noteCount > 0; }
    private get canAddNotes(): boolean { return !this.disableNotes && !this.disabled; }
    private get canAddAttachments(): boolean { return !this.disableAttachments && !this.disabled; }

    get areAttachmentsSetup(): boolean { return this.hasTCM !== undefined; }
    get hasCommands(): boolean { return this.commands?.some(x => x.isVisible) || false; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const addNoteCommand: Command = new LabelCommand({
            label: "Add Notes",
            action: () => this.addNotes(),
            getVisible: () => this.canAddNotes,
        });

        const attachmentCommand: Command = new LabelCommand({
            label: () => this.hasTCM && this.hasAttachments
                ? "Attachments"
                : "Add Attachments",
            action: () => this.onAddAttachment(),
            getVisible: () => this.canAddAttachments,
        });
        this.commands = [
            addNoteCommand,
            attachmentCommand,
            ...this.additionalCommands
        ];

        this.entityAddMode = Utilities.IsNullOrUndefined(this.entity.Id);
        this.getDocumentCount();
        this.updateNoteCount();

        if (Utilities.IsNullOrUndefined(this.chips))
        {
            this.chips = [];
        }

        this.chips.push(
            new DynamicChip(
                () => `Attachments(${this.attachmentCount})`,
                () => this.showAttachments(),
                () => !this.disableAttachments && this.hasAttachments
            ),
            new DynamicChip(
                () => `Notes(${this.noteCount})`,
                () => this.showNotes(),
                () => !this.disableNotes && this.hasNotes
            )
        );
    }

    protected getDocumentCount(): Promise<void>
    {
        if (this.disableAttachments !== false)
        {
            this.attachmentCount = 0;
            return;
        }

        if (!this.areAttachmentsSetup)
        {
            return this.initializeDocumentCount();
        }
        else
        {
            return this.updateDocumentCount();
        }
    }

    private async updateDocumentCount(): Promise<void>
    {
        if (this.entityAddMode)
        {
            this.attachmentCount = this.localAttachments?.length || 0;
            return;
        }

        const criteria: DocumentSearchCriteria = {
            EntityId: this.entity.Id,
            EntityType: this.entity.Type,
            // Entity needed for IV PCard attachments without an associated invoice
            Entity: this.tcmModel
        };

        return this.$documentApiService.GetDocumentCount(criteria)
            .then(
                result =>
                {
                    this.attachmentCount = result.Result;
                },
                () =>
                {
                    this.attachmentCount = 0;
                });
    }

    private async initializeDocumentCount(): Promise<void>
    {
        return this.$documentApiService.HasTcm()
            .then(
                (executionResult: ExecutionResult<boolean>) =>
                {
                    this.hasTCM = executionResult.Result;

                    return this.updateDocumentCount();
                },
                (error: unknown) =>
                {
                    console.error("Unknown error", error);
                    this.disableAttachments = true;
                });
    }

    protected async updateNoteCount(): Promise<void>
    {
        if (this.disableNotes !== false)
        {
            return;
        }

        return this.$notesService.Get(this.entity)
            .then(
                (executionResult: ExecutionResult<Array<NoteViewModel>>): void =>
                {
                    this.noteCount = executionResult.Result.length;
                },
                (_error: unknown): void =>
                {
                    this.disableNotes = true;
                });
    }

    protected addNotes(): void
    {
        if (EventEmitterHelper.hasSubscribers(this.addNote))
        {
            this.addNote.emit();
            return;
        }

        this.$notesDialogService.show(
            this.entity,
            true,
            false,
            undefined,
            undefined,
            undefined,
            this.hideSubject)
            .then((result: Array<INote>) =>
            {
                this.noteCount += result.length;
            });
    }

    protected onAddAttachment(): void
    {
        if (EventEmitterHelper.hasSubscribers(this.addAttachment))
        {
            this.addAttachment.emit();
            return;
        }

        // TODO - move this to dialogService?
        this.$advancedComponentsDialogService.showAddAttachment(
            {
                attachments: this.localAttachments,
                disabled: this.disabled,
                entity: this.entity,
                hasTCM: this.hasTCM,
                tcmModel: this.tcmModel
            })
            .then(attachments =>
            {
                this.getDocumentCount();
                if (!attachments)
                {
                    return;
                }

                this.localAttachmentsChange.emit(attachments);
            });
    }

    protected showAttachments(): void
    {
        this.$attachmentsDialogService.show(
            this.entity.Id,
            this.entity.Type,
            false,
            this.tcmModel,
            this.localAttachments,
            this.disabled)
            .then(() => this.getDocumentCount());
    }

    protected showNotes(): void
    {
        this.$notesDialogService.show(
            this.entity,
            false,
            this.disabled,
            undefined,
            undefined,
            undefined,
            this.hideSubject)
            .then((result) =>
            {
                this.noteCount = result.length;
            });
    }
}
